import { useState } from 'react';

import { TMSEquipmentType } from 'types/Load';
import { MaybeUndef } from 'types/UtilityTypes';
import { useThrowableSWR } from 'utils/fetcher';

type SpecialReferencesResponse = TMSEquipmentType[];

type FetchSpecialReferencesOptions = {
  customerName?: MaybeUndef<string>;
  name: string;
};

function buildSpecialReferencesKey({
  tmsID,
  name,
  customerScoped,
  customerName,
}: {
  tmsID: number;
  name: string;
  customerScoped: boolean;
  customerName: string;
}): string | null {
  if (!tmsID || !name) {
    return null;
  }
  if (customerScoped && !customerName) {
    return null;
  }

  const query = `tmsID=${tmsID}&name=${encodeURIComponent(name)}`;
  if (customerScoped) {
    return `load/options/special-references?${query}&customerName=${encodeURIComponent(customerName)}`;
  }

  return `load/options/special-references?${query}`;
}

export function useFetchSpecialReferences(
  tmsID: number,
  options?: FetchSpecialReferencesOptions
) {
  const customerScoped = options !== undefined;
  const customerName = options?.customerName?.trim() ?? '';
  const name = options?.name?.trim() ?? '';

  const [isRefreshing, setIsRefreshing] = useState(false);

  const key = buildSpecialReferencesKey({
    tmsID,
    name,
    customerScoped,
    customerName,
  });

  const { data, error, isLoading, mutate } =
    useThrowableSWR<SpecialReferencesResponse>(
      key,
      false // Don't retry on 404s
    );

  const refresh = async () => {
    setIsRefreshing(true);
    try {
      await mutate();
    } finally {
      setIsRefreshing(false);
    }
  };

  return {
    specialReferences: data,
    isLoading: (Boolean(key) && isLoading) || isRefreshing,
    error,
    refresh,
  };
}
